import type {
  ActionOutputData,
  InputData,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { translateAction } from "../../../../../locales/i18n";
import { useRootStore } from "../../../../../stores/hooks";
import type { ActionCandidate } from "../../../../../types/candidate";
import { getErrorMessage } from "../../../ActivityGrid/ActivityGrid.helpers";
import { ActionError } from "../ActionError/ActionError";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import { useRecordJobData } from "../../hooks/useRecordJobData";
import { GeneralActionResultGroupWrapper } from "../GeneralActionResultGroupWrapper/GeneralActionResultGroupWrapper";

import { isOldTypejob } from "../../utils";
import { getActionRecordCount } from "../../../shared/status-walk";
import type { JobDetails } from "../ActivityDetails.types";

interface GeneralActionResultGroupProps {
  action: ActionOutputData;
  /** Top-level group the action came from; record-job paths are keyed by it. */
  groupInstanceId?: number;
  getInputValue?: (
    input: InputData,
    jobDetails: JobDetails,
    recordJob?: RecordJobData,
  ) => string;
  shouldDisplayInput?: (input: InputData, recordJob?: RecordJobData) => boolean;
}

export const GeneralActionResultGroup = observer<GeneralActionResultGroupProps>(
  ({ action, groupInstanceId = 0, getInputValue, shouldDisplayInput }) => {
    const { integrations, automations, jobDetails } = useRootStore();
    const selectedIntegration = integrations.list.find(
      (i) => i.integrationId === action.actionReference?.integrationId,
    );
    const selectedAction = selectedIntegration?.actions.find(
      (a) => a.actionId === action.actionReference?.actionId,
    );
    const [selectedActionFallback, setSelectedActionFallback] = useState<
      ActionCandidate | undefined
    >();
    const [recordJobsFiltered, setRecordJobsFiltered] = useState<
      RecordJobData[]
    >([]);
    const [recordJobData, setRecordJobData] = useState<
      Record<string, RecordJobData>
    >({});

    useEffect(() => {
      setSelectedActionFallback(
        automations.candidate.allActions.find(
          (actionInput: ActionCandidate) =>
            actionInput.actionReference?.instanceId ===
            action.actionReference?.instanceId,
        ),
      );
    }, [automations.candidate]);

    useEffect(() => {
      const recordJobDatas = jobDetails.recordJobData;
      setRecordJobsFiltered(
        recordJobDatas.filter(
          (recordJobData) =>
            recordJobData.actionReference?.instanceId ===
            action.actionReference?.instanceId,
        ),
      );
    }, [JSON.stringify(jobDetails.recordJobData)]);

    const { fetchRecordJobData } = useRecordJobData({
      recordJobData,
      setRecordJobData,
    });

    const status = action.status.oneofKind === "ok" ? "is_success" : "is_error";

    const errorMessage = getErrorMessage(action, jobDetails.job);
    const oldTypeJob = useMemo(
      () => isOldTypejob(jobDetails),
      [jobDetails.job],
    );
    const onDetailsClickWithFetch = async (
      recordJodId: string,
    ): Promise<void> => {
      await fetchRecordJobData(recordJodId);
    };

    const recordCount = getActionRecordCount(action, jobDetails.job);

    return (
      <ActivityDetailsGroup
        label={
          selectedAction !== undefined ? translateAction(selectedAction) : ""
        }
        status={status}
      >
        {status === "is_error" && <ActionError errorMessage={errorMessage} />}

        {!oldTypeJob &&
          recordCount > 0 &&
          Array.from({ length: recordCount }).map((_, idx) => {
            const recordJodId = `jobs/${
              jobDetails.job.jobId
            }/group-${groupInstanceId}/action-${
              action.actionReference?.instanceId ?? ""
            }/record-${idx}`;
            return (
              <GeneralActionResultGroupWrapper
                key={idx}
                index={idx}
                jobDetails={jobDetails}
                recordJob={recordJobData[recordJodId]}
                onDetailsClickWithFetch={onDetailsClickWithFetch}
                recordJodId={recordJodId}
                action={action}
                automations={automations}
                selectedActionFallback={selectedActionFallback}
                getInputValue={getInputValue}
                shouldDisplayInput={shouldDisplayInput}
              />
            );
          })}

        {oldTypeJob &&
          recordJobsFiltered.map((recordJob, index) => {
            const recordJodId = `jobs/${jobDetails.job.jobId}/group-0/action-${
              action.actionReference?.instanceId ?? ""
            }/record-${index}`;
            return (
              <GeneralActionResultGroupWrapper
                key={recordJodId}
                index={index}
                jobDetails={jobDetails}
                recordJob={recordJob}
                action={action}
                automations={automations}
                selectedActionFallback={selectedActionFallback}
                getInputValue={getInputValue}
                shouldDisplayInput={shouldDisplayInput}
              />
            );
          })}
      </ActivityDetailsGroup>
    );
  },
);
